import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Btn, Wordmark, Ornament, Tagline } from "@/components/brand";
import { useSeo } from "@/components/site";
import { GoogleButton } from "@/components/google";

function OrDivider() {
  return <div className="flex items-center gap-3 my-6"><span className="h-px flex-1 bg-white/15" /><span className="f-mono text-[10px] tracking-[0.2em] text-white/50">OR</span><span className="h-px flex-1 bg-white/15" /></div>;
}

function Shell({ children, title }) {
  useSeo(title);
  return (
    <div className="min-h-screen bg-ink text-white grid lg:grid-cols-2">
      <div className="hidden lg:flex items-center justify-center p-12 border-r border-white/10">
        <div className="max-w-[340px] w-full">
          <div className="border border-gold/50 p-3"><img src="/brand/logo.jpg" alt="Se7en Capital Group logo" className="w-full h-auto object-contain drop-shadow-[0_12px_24px_rgba(0,0,0,.6)]" /></div>
        </div>
      </div>
      <div className="flex flex-col justify-center px-6 sm:px-16 py-12">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-block mb-10" data-testid="auth-home-link"><Wordmark dark size="lg" /></Link>
          <img src="/brand/logo.jpg" alt="" className="lg:hidden w-28 mb-8 border border-gold/40 p-1.5" />
          {children}
          <Ornament className="mt-12 mb-4" />
          <p className="f-mono text-[9px] tracking-[0.2em] text-gold text-center">BUILDING BETTER CREDIT · FUNDING YOUR FUTURE · CREATING FINANCIAL FREEDOM</p>
        </div>
      </div>
    </div>
  );
}

const darkField = "field !bg-white/5 !border-white/20 text-white placeholder:text-white/40 focus:!border-gold";

function DField({ label, id, ...p }) {
  return (
    <div>
      <label htmlFor={id} className="label !text-white/60">{label}</label>
      <input id={id} className={darkField} data-testid={id} {...p} />
    </div>
  );
}

function Err({ msg }) {
  return msg ? <p className="text-sm bg-red7/20 text-white border-l-2 border-red7b px-4 py-3" role="alert" data-testid="auth-error">{msg}</p> : null;
}

export function LoginPage({ owner = false }) {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const home = owner ? "/owner" : "/portal";
  useEffect(() => {
    if (user && ((owner && user.role !== "client") || (!owner && user.role === "client"))) nav(home, { replace: true });
  }, [user, owner, nav, home]);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password, owner ? "owner" : "client");
      nav(loc.state?.from || home, { replace: true });
    } catch (ex) { setErr(errMsg(ex, "Couldn't sign in. Try again.")); } finally { setBusy(false); }
  };
  return (
    <Shell title={owner ? "Owner Login" : "Client Login"}>
      <p className="eyebrow text-gold">{owner ? "Owner portal" : "Client portal"}</p>
      <h1 className="f-display text-4xl mt-3">{owner ? "Welcome back, Seven." : "Sign in to your portal."}</h1>
      <div className="mt-8"><GoogleButton owner={owner} /></div>
      <OrDivider />
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <DField label="Email" id="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
        <DField label="Password" id="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
        <Err msg={err} />
        <Btn type="submit" variant="gold" arrow className="w-full" disabled={busy} data-testid="login-submit-btn">{busy ? "Signing in…" : "Sign In"}</Btn>
      </form>
      <div className="flex flex-wrap justify-between gap-3 mt-6 text-sm">
        <Link to="/forgot-password" className="text-white/70 hover:text-gold" data-testid="forgot-link">Forgot password?</Link>
        {owner ? <Link to="/login" className="text-white/70 hover:text-gold" data-testid="switch-login-link">Client login →</Link>
          : <span className="text-white/70">New client? <Link to="/register" className="text-gold" data-testid="register-link">Create your login</Link></span>}
      </div>
      {!owner && <Link to="/owner/login" className="block mt-3 text-xs text-white/40 hover:text-gold" data-testid="switch-owner-link">Owner login</Link>}
    </Shell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [q] = useSearchParams();
  const [f, setF] = useState({ name: "", email: q.get("email") || "", phone: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try { await register(f); nav("/portal", { replace: true }); } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Create Portal Login">
      <p className="eyebrow text-gold">New client intake</p>
      <h1 className="f-display text-4xl mt-3">Create your portal login.</h1>
      <p className="text-white/60 mt-3 text-sm">Use the same email you gave me. Your portal opens once I review your account.</p>
      <div className="mt-8"><GoogleButton /></div>
      <OrDivider />
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <DField label="Full name" id="reg-name" value={f.name} onChange={set("name")} required autoComplete="name" />
        <DField label="Email" id="reg-email" type="email" value={f.email} onChange={set("email")} required autoComplete="email" />
        <DField label="Phone" id="reg-phone" type="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" />
        <DField label="Password (8+ characters)" id="reg-password" type="password" minLength={8} value={f.password} onChange={set("password")} required autoComplete="new-password" />
        <Err msg={err} />
        <Btn type="submit" variant="gold" arrow className="w-full" disabled={busy} data-testid="register-submit-btn">{busy ? "Creating…" : "Create Login"}</Btn>
      </form>
      <p className="mt-6 text-sm text-white/70">Already have a login? <Link to="/login" className="text-gold">Sign in</Link></p>
    </Shell>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (ex) { setErr(errMsg(ex)); }
  };
  return (
    <Shell title="Reset Password">
      <h1 className="f-display text-4xl">Reset your password.</h1>
      {sent ? <p className="text-white/80 mt-6" data-testid="forgot-sent">If that email has an account, a reset link is on its way. It expires in 1 hour.</p> : (
        <form onSubmit={submit} className="space-y-5 mt-8" data-testid="forgot-form">
          <DField label="Email" id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Err msg={err} />
          <Btn type="submit" variant="gold" className="w-full" data-testid="forgot-submit-btn">Send Reset Link</Btn>
        </form>
      )}
      <Link to="/login" className="block mt-6 text-sm text-white/70 hover:text-gold">← Back to sign in</Link>
    </Shell>
  );
}

export function ResetPage() {
  const [q] = useSearchParams();
  const [pw, setPw] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: q.get("token"), password: pw }); setDone(true); } catch (ex) { setErr(errMsg(ex)); }
  };
  return (
    <Shell title="Choose a New Password">
      <h1 className="f-display text-4xl">Choose a new password.</h1>
      {done ? <p className="mt-6 text-white/80" data-testid="reset-done">Password updated. <Link className="text-gold" to="/login">Sign in →</Link></p> : (
        <form onSubmit={submit} className="space-y-5 mt-8" data-testid="reset-form">
          <DField label="New password (8+ characters)" id="reset-password" type="password" minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} required />
          <Err msg={err} />
          <Btn type="submit" variant="gold" className="w-full" data-testid="reset-submit-btn">Update Password</Btn>
        </form>
      )}
    </Shell>
  );
}

export function VerifyPage() {
  const [q] = useSearchParams();
  const [state, setState] = useState("working");
  useEffect(() => {
    api.post("/auth/verify-email", { token: q.get("token") }).then(() => setState("ok")).catch(() => setState("bad"));
  }, [q]);
  return (
    <Shell title="Confirm Email">
      <h1 className="f-display text-4xl" data-testid="verify-status">{state === "ok" ? "Email confirmed." : state === "bad" ? "This link is invalid or already used." : "Confirming…"}</h1>
      <Btn as={Link} to="/portal" variant="gold" arrow className="mt-8">Go to my portal</Btn>
    </Shell>
  );
}
